import { UserProfile } from './types/UserProfile';

type Loose = Record<string, unknown>;

function asLoose(v: unknown): Loose | null {
  if (v == null || typeof v !== 'object' || Array.isArray(v)) return null;
  return v as Loose;
}

/** Coerce API boolean-ish values (bool, 0/1, "true"/"false"). */
export function coerceNotificationBoolean(value: unknown, whenMissing: boolean): boolean {
  if (value === undefined || value === null) return whenMissing;
  if (typeof value === 'boolean') return value;
  if (typeof value === 'number') return value !== 0;
  if (typeof value === 'string') {
    const s = value.trim().toLowerCase();
    if (['true', '1', 'yes', 'y'].includes(s)) return true;
    if (['false', '0', 'no', 'n'].includes(s)) return false;
  }
  return whenMissing;
}

const EMAIL_KEYS = ['email_opt_in', 'emailOptIn'];
const SMS_KEYS = ['sms_opt_in', 'smsOptIn'];

function pick(obj: Loose | null | undefined, keys: string[]): unknown {
  if (!obj) return undefined;
  for (const k of keys) {
    if (k in obj && obj[k] !== undefined) return obj[k];
  }
  return undefined;
}

/** `user` / `profile` / `data` node inside a typical API envelope. */
function innerFromRaw(raw: unknown): Loose {
  const root = asLoose(raw) ?? {};
  return (
    asLoose(root.user) ??
    asLoose(root.profile) ??
    asLoose(root.data) ??
    root
  );
}

/**
 * Ordered list of objects that may carry notification toggles (user row, envelope, patient_profile, prefs blocks).
 * Same object is not added twice (by reference).
 */
function notificationSourceObjects(raw: unknown, userBlob: Loose): Loose[] {
  const root = asLoose(raw) ?? {};
  const inner = innerFromRaw(raw);
  const out: Loose[] = [];

  const add = (x: unknown): void => {
    const o = asLoose(x);
    if (o && !out.includes(o)) out.push(o);
  };

  add(userBlob);
  add(inner);
  add(root);

  for (const base of [userBlob, inner, root]) {
    add(base.patient_profile);
    add(base.patientProfile);

    const blocks = [
      asLoose(base.notification_preferences) ?? asLoose(base.notificationPreferences),
      asLoose(base.notification_settings) ?? asLoose(base.notificationSettings),
    ];
    for (const block of blocks) {
      add(block);
      if (block) {
        add(block.notifications);
        add(block.channels);
      }
    }
  }

  return out;
}

function pickEmailSmsFromSources(sources: Loose[]): { emailRaw: unknown; smsRaw: unknown } {
  let emailRaw: unknown;
  let smsRaw: unknown;
  for (const o of sources) {
    if (emailRaw === undefined) emailRaw = pick(o, EMAIL_KEYS);
    if (smsRaw === undefined) smsRaw = pick(o, SMS_KEYS);
    if (emailRaw !== undefined && smsRaw !== undefined) break;
  }
  return { emailRaw, smsRaw };
}

/**
 * Read toggles from GET payloads. `userBlob` is usually the normalized user object (`d` from the response).
 * Wrapper-level fields on `raw` are still scanned.
 */
export function parseCollectorUserNotificationPrefs(
  raw: unknown,
  userBlob: Record<string, unknown>,
): { email_opt_in: boolean; sms_opt_in: boolean } {
  const sources = notificationSourceObjects(raw, userBlob as Loose);
  const { emailRaw, smsRaw } = pickEmailSmsFromSources(sources);
  return {
    email_opt_in: coerceNotificationBoolean(emailRaw, true),
    sms_opt_in: coerceNotificationBoolean(smsRaw, false),
  };
}

/** Same as {@link parseCollectorUserNotificationPrefs} with `userBlob` = inner node of `raw`. */
export function parseNotificationPrefsFromApiRoot(raw: unknown): { email_opt_in: boolean; sms_opt_in: boolean } {
  return parseCollectorUserNotificationPrefs(raw, innerFromRaw(raw) as Record<string, unknown>);
}

/** Read prefs from a normalized UserProfile (after merge from API). */
export function notificationPrefsFromUser(user: UserProfile | null | undefined): {
  email_opt_in: boolean;
  sms_opt_in: boolean;
} {
  console.log('notificationPrefsFromUser', user);
  if (!user) {
    return { email_opt_in: true, sms_opt_in: false };
  }
  const u = user as UserProfile & { email_opt_in?: unknown; sms_opt_in?: unknown };
  const pp = asLoose(user.patient_profile as unknown);

  const emailRaw =
    u.email_opt_in !== undefined && u.email_opt_in !== null ? u.email_opt_in : pick(pp, EMAIL_KEYS);
  const smsRaw =
    u.sms_opt_in !== undefined && u.sms_opt_in !== null ? u.sms_opt_in : pick(pp, SMS_KEYS);

  return {
    email_opt_in: coerceNotificationBoolean(emailRaw, true),
    sms_opt_in: coerceNotificationBoolean(smsRaw, false),
  };
}

/**
 * Only keys that exist on `user` (non-nullish). Use in UI sync so a PATCH/refetch that omits these fields
 * does not reset switches back to defaults.
 */
export function pickDefinedNotificationPrefs(user: UserProfile | null | undefined): {
  email_opt_in?: boolean;
  sms_opt_in?: boolean;
} {
  if (!user) return {};
  const u = user as UserProfile & { email_opt_in?: unknown; sms_opt_in?: unknown };
  const out: { email_opt_in?: boolean; sms_opt_in?: boolean } = {};
  if (u.email_opt_in !== undefined && u.email_opt_in !== null) {
    out.email_opt_in = coerceNotificationBoolean(u.email_opt_in, true);
  }
  if (u.sms_opt_in !== undefined && u.sms_opt_in !== null) {
    out.sms_opt_in = coerceNotificationBoolean(u.sms_opt_in, false);
  }
  return out;
}
